import React from 'react';
import { useAppContext } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
const { FiMove, FiMaximize, FiTrendingUp, FiZap, FiShield, FiLink, FiDollarSign, FiArrowRight } = FiIcons;

export default function Sidebar() {
  const { tiles, mode, setMode, pixelsSold, setCurrentPage } = useAppContext();
  const topTiles = [...tiles]
    .filter(t => t.title && t.link)
    .sort((a, b) => (b.w * b.h) - (a.w * a.h))
    .slice(0, 5);
  const percentSold = ((pixelsSold / 1000000) * 100).toFixed(1);

  return (
    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-60 z-20 flex flex-col gap-3 pointer-events-auto">
      <div className="rounded-xl overflow-hidden border border-pink-500/20" style={{ background: 'rgba(10,0,20,0.95)', backdropFilter: 'blur(20px)' }}>
        {/* Mode toggle */}
        <div className="p-3 border-b border-pink-500/10">
          <div className="text-gray-500 uppercase tracking-widest font-bold mb-2" style={{ fontSize: '11px' }}>Grid Mode</div>
          <div className="flex rounded-lg overflow-hidden border border-gray-800">
            <button onClick={() => setMode('pan')}
              className={`flex-1 py-2 font-bold flex items-center justify-center gap-1.5 transition-all ${mode === 'pan' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
              style={{ fontSize: '13px', ...(mode === 'pan' ? { background: 'rgba(255,0,200,0.15)', color: '#ff00cc', borderRight: '1px solid rgba(255,0,200,0.2)' } : { borderRight: '1px solid rgba(255,255,255,0.05)' }) }}>
              <FiMove className="text-xs" /> PAN
            </button>
            <button onClick={() => setMode('select')}
              className={`flex-1 py-2 font-bold flex items-center justify-center gap-1.5 transition-all ${mode === 'select' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
              style={{ fontSize: '13px', ...(mode === 'select' ? { background: 'rgba(255,0,200,0.15)', color: '#ff00cc' } : {}) }}>
              <FiMaximize className="text-xs" /> SELECT
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="p-3 border-b border-pink-500/10">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-gray-500 uppercase tracking-widest" style={{ fontSize: '11px' }}>Filled</span>
            <span className="font-black" style={{ color: '#ff00cc', fontSize: '14px' }}>{percentSold}%</span>
          </div>
          <div className="h-2 bg-gray-900 rounded-full overflow-hidden border border-pink-500/10">
            <div className="h-full rounded-full transition-all" style={{ width: `${percentSold}%`, background: 'linear-gradient(90deg,#ff00cc,#cc00ff)' }} />
          </div>
          <div className="text-gray-600 mt-1" style={{ fontSize: '12px' }}>{(1000000 - pixelsSold).toLocaleString()} px remaining</div>
        </div>

        {/* Persuasion nudge */}
        <div className="p-3 border-b border-pink-500/10" style={{ background: 'rgba(255,0,200,0.04)' }}>
          <div className="flex items-start gap-2">
            <FiZap className="text-pink-500 text-xs mt-0.5 shrink-0 animate-blink" />
            <p className="leading-relaxed" style={{ color: '#e2e8f0', fontSize: '13px' }}>
              Prices <span className="text-pink-400 font-black">rise every tier</span>. Buy now and lock in the lowest price forever.
            </p>
          </div>
        </div>

        {/* Affiliate nudge */}
        <div className="p-3 border-b border-pink-500/10 cursor-pointer hover:bg-white/5 transition-colors" onClick={() => setCurrentPage('affiliate')}
          style={{ background: 'rgba(0,255,136,0.03)' }}>
          <div className="flex items-center gap-2">
            <FiDollarSign className="text-green-400 text-xs shrink-0" />
            <p style={{ color: '#e2e8f0', fontSize: '12px' }}>
              Refer friends → earn <span className="font-black" style={{ color: '#00ff88' }}>20% commission</span>
            </p>
            <FiArrowRight className="text-green-400 text-xs ml-auto shrink-0" />
          </div>
        </div>

        {/* Leaderboard */}
        <div className="p-3">
          <div className="text-gray-500 uppercase tracking-widest font-bold mb-2 flex items-center gap-1" style={{ fontSize: '11px' }}>
            <FiTrendingUp className="text-pink-500" /> Top By Pixels
          </div>
          <div className="space-y-2">
            {topTiles.map((tile, idx) => (
              <div key={tile.id} className="flex items-center gap-2 group cursor-pointer"
                onClick={() => tile.link && window.open(tile.link, '_blank', 'noopener,noreferrer')}>
                <div className="w-5 text-center font-black" style={{ fontSize: '13px', color: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : '#ff00cc' }}>
                  {idx + 1}
                </div>
                <div className="w-7 h-7 rounded overflow-hidden shrink-0 border border-gray-800 group-hover:border-pink-500/50 transition-colors" style={{ background: tile.solidColor || '#1a0030' }}>
                  {tile.image ? <img src={tile.image} alt={tile.title} className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: tile.solidColor || '#1a0030' }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-300 truncate group-hover:text-white transition-colors" style={{ fontSize: '13px' }}>{tile.title}</div>
                  <div className="text-gray-600" style={{ fontSize: '11px' }}>{(tile.w * tile.h).toLocaleString()} px</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mini CTA */}
        <div className="p-3 pt-0">
          <button onClick={() => setMode('select')}
            className="w-full py-2.5 rounded-lg font-black uppercase tracking-widest text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-1"
            style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', fontFamily: 'Impact,sans-serif', fontSize: '14px' }}>
            <FiZap className="text-xs" /> Claim Your Spot
          </button>
        </div>
      </div>

      {/* Trust badges */}
      <div className="rounded-xl p-3 border border-pink-500/10 space-y-2" style={{ background: 'rgba(10,0,20,0.9)', backdropFilter: 'blur(20px)' }}>
        {[
          { icon: FiShield, text: 'Permanent — never expires' },
          { icon: FiTrendingUp, text: 'Real SEO dofollow link' },
          { icon: FiLink, text: 'Your brand, forever visible' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-gray-400" style={{ fontSize: '12px' }}>
            <item.icon className="text-pink-500 shrink-0 text-xs" /> {item.text}
          </div>
        ))}
      </div>
    </div>
  );
}